import { Button, Input, Modal, Typography } from 'antd';
import React from 'react';
import { Profile } from 'oidc-client';
import { isUserAdmin } from '../../../helpers/CheckPermissionHelper';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { ApiException, CompetitionDetailVm, CompetitionsClient, DeleteCompetitionMatchesResultVm } from '../../../utils/api';

interface Props {
    competition: CompetitionDetailVm;
    userProfile: Profile;
    onCompleted?: () => Promise<void>;
}

interface State {
    visible: boolean;
    keepList: string;
    confirmCompetitionId: string;
    submitting: boolean;
    lastResult?: DeleteCompetitionMatchesResultVm;
}

class CompetitionAdminBulkDeleteMatches extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            visible: false,
            keepList: '',
            confirmCompetitionId: '',
            submitting: false,
        };
    }

    private open = (): void => {
        this.setState({
            visible: true,
            keepList: '',
            confirmCompetitionId: '',
            lastResult: undefined,
        });
    };

    private close = (): void => {
        this.setState({ visible: false, submitting: false });
    };

    private submit = async (): Promise<void> => {
        const { competition, onCompleted } = this.props;
        const { keepList, confirmCompetitionId } = this.state;
        if (confirmCompetitionId.trim() !== String(competition.id)) {
            return;
        }

        this.setState({ submitting: true });
        try {
            const client = new CompetitionsClient();
            const result = await client.deleteMatches(competition.id, keepList.trim() || undefined);
            this.setState({ lastResult: result });
            const count = result?.deletedCount ?? 0;
            const ids = result?.deletedMatchIds?.length
                ? result.deletedMatchIds.join(', ')
                : 'none';
            success(`Soft-deleted ${count} match(es): ${ids}.`);
            this.close();
            if (onCompleted) {
                await onCompleted();
            }
        } catch (error) {
            this.setState({ submitting: false });
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Error deleting matches.');
            }
        }
    };

    public render(): React.ReactElement | null {
        const { competition, userProfile } = this.props;
        const { visible, keepList, confirmCompetitionId, submitting } = this.state;
        if (!userProfile || !isUserAdmin(userProfile)) {
            return null;
        }

        const confirmReady = confirmCompetitionId.trim() === String(competition.id);

        return (
            <>
                <div style={{ margin: '16px 0' }}>
                    <Typography.Text type="secondary">Admin only.</Typography.Text>
                    <Button danger style={{ marginLeft: 8 }} onClick={this.open}>
                        Bulk delete competition matches
                    </Button>
                </div>
                <Modal
                    title="Bulk delete competition matches"
                    visible={visible}
                    okText="Delete matches"
                    okButtonProps={{ disabled: !confirmReady, danger: true, loading: submitting }}
                    cancelButtonProps={{ disabled: submitting }}
                    onOk={(): void => void this.submit()}
                    onCancel={this.close}
                    destroyOnClose
                >
                    <p>
                        Soft-deletes every non-deleted match in competition {competition.id} except
                        the match ids you list in keep, including their events and lineups. This is
                        refused while any match in the competition is in progress.
                    </p>
                    <p>Keep match ids (comma-separated, optional)</p>
                    <Input
                        placeholder="e.g. 101, 102, 205"
                        value={keepList}
                        onChange={(event): void => this.setState({ keepList: event.target.value })}
                    />
                    <p style={{ marginTop: 16 }}>Type {competition.id} to confirm.</p>
                    <Input
                        value={confirmCompetitionId}
                        onChange={(event): void =>
                            this.setState({ confirmCompetitionId: event.target.value })
                        }
                        onPressEnter={(): void => {
                            if (confirmReady) {
                                void this.submit();
                            }
                        }}
                    />
                </Modal>
            </>
        );
    }
}

export default CompetitionAdminBulkDeleteMatches;
